import { useCallback, useEffect, useRef, useState, createContext } from "react";
import { ApplicationContextType, LendingJobType, monthlyReportRoutes, MonthlyReportSteps, UnderwritingColdState, UnderwritingFormStatus, UnderwritingSteps, uploadStates } from "../../../interfaces/interfaces";
import { useNavigate } from "react-router";
import { RestS } from "../../../rest/rest-service";
import styles from "./MonthlyReportFormContext.module.scss";
import CircularLoader from "../../../components/common/CircularLoader/CircularLoader";
import { getLastThreeFinancialYears, UnderwritingFormContextType } from "../../Underwriting/context/UnderwritingFormContext";
import type { TallyAppConnectionStatus } from "../../../rest/gst-rest-service";
import _ from "lodash";
import { useAppDispatch, useAppSelector } from "../../../redux/reduxHooks";
import { setRefreshFormData, setRefreshRevenueClassification, setRefreshStatementJobs } from "../../../redux/underwriting/underwriting";
import { useGstJobTracking } from "../../../components/Hooks/useGstJobTracking";


export type MonthlyReportFormContextType = {
    formData: { [x: string]: any },
    formStepsValidity: { [x: string]: boolean },
    setFormData: (data: any) => void,
    formRefs: React.MutableRefObject<{ [key: string]: any }>;
    brandCategoriesObj: { [x: string]: { id: number, category: string } },
    setStepValidity: (step: any, isValid: boolean) => void;
    formCompleteStatus: { [x: string]: UnderwritingFormStatus };
    handleAndSetFormStatuses: (formStatuses: any) => void;
    uploadingPendingItems: any[];
    changeUploadStatus: (keyName: string, uploadStatus: uploadStates) => void;
    getUploadStatus: (keyName: string) => uploadStates;
    underwritingFileJobs: { [key in MonthlyReportSteps]: {
        [x: string]: any
    } };
    handleUnderwritingFileJobs: (step: UnderwritingSteps | MonthlyReportSteps, fieldName: string, jobDetails: any) => void;
    sendFileJobToApi: (step: UnderwritingSteps | MonthlyReportSteps, fieldName: string, jobDetails: any) => Promise<any>;
    statementAnalysisJobs: { [key: string]: any };
    fetchUnderwritingFileJobs: () => void;
    fetchStatementAnalysisJobs: () => void;
    revenueClassificationJobs: { [fyYear: string]: any };
    fetchRevenueClassificationJobs: (fyYear?: string) => void;
    clearRevenueClassificationJob: (fyYear: string) => void;
    clearAllRevenueClassificationData: (fyYear: string) => void;
    gstAnalysisJobs: { [key: string]: any };
    setGstAnalysisJobs: (jobs: any) => void;
    fetchGstAnalysisJobs: () => void;
    extraFormDetails: any;
    isTallyConnected?: boolean;
    tallyLastSyncedAt?: string | null;
    tallySynced?: boolean;
    tallyConnection?: TallyAppConnectionStatus | null;
    fetchTallyConnectionStatus?: () => void;
}

export const MonthlyReportFormContext = createContext<MonthlyReportFormContextType | UnderwritingFormContextType>(null)

const MonthlyReportFromProvider = ({ children }) => {
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({});
    const [brandCategoriesObj, setBrandCategoriesObj] = useState({});

    const { refreshFormData, refreshStatementJobs, refreshRevenueClassification } = useAppSelector(state => state.underwriting)
    const dispatch = useAppDispatch();

    const [formStepsValidity, setFormStepsValidity] = useState({
        [MonthlyReportSteps.connectAccounts]: false,
        [MonthlyReportSteps.bankStatement]: false,
        [MonthlyReportSteps.salesMarketing]: false,
        [MonthlyReportSteps.businessUpdates]: false,
        [MonthlyReportSteps.gst]: false,
        [MonthlyReportSteps.recipients]: false,
    });

    const [formCompleteStatus, setFormCompleteStatus] = useState({
        [MonthlyReportSteps.connectAccounts]: UnderwritingFormStatus.INCOMPLETE,
        [MonthlyReportSteps.bankStatement]: UnderwritingFormStatus.INCOMPLETE,
        [MonthlyReportSteps.salesMarketing]: UnderwritingFormStatus.INCOMPLETE,
        [MonthlyReportSteps.businessUpdates]: UnderwritingFormStatus.INCOMPLETE,
        [MonthlyReportSteps.gst]: UnderwritingFormStatus.INCOMPLETE,
        [MonthlyReportSteps.recipients]: UnderwritingFormStatus.INCOMPLETE,
    });

    const [underwritingFileJobs, setUnderwritingFileJobs] = useState({
        [MonthlyReportSteps.connectAccounts]: {},
        [MonthlyReportSteps.bankStatement]: {},
        [MonthlyReportSteps.businessUpdates]: {},
        [MonthlyReportSteps.gst]: {},
        [MonthlyReportSteps.recipients]: {},
        [MonthlyReportSteps.salesMarketing]: {},
    });

    const [statementAnalysisJobs, setStatementAnalysisJobs] = useState({});

    const { gstAnalysisJobs, setGstAnalysisJobs, fetchGstAnalysisJobs } = useGstJobTracking();

    const underwritingFileJobsRef = useRef(underwritingFileJobs);

    const [uploadingPendingItems, setUploadPendingItems] = useState<any[]>([]);
    const [fileUploadingStatus, setFileUploadingStatus] = useState({});

    const [extraFormDetails, setExtraFormDetails] = useState<any>({});

    const [revenueClassificationJobs, setRevenueClassificationJobs] = useState<{ [fyYear: string]: any }>({});


    const handleUnderwritingFileJobs = (step: MonthlyReportSteps, fieldName: string, jobDetails: any) => {
        console.log('jobDetails', jobDetails)
        setUnderwritingFileJobs((prev) => ({
            ...prev,
            [step]: {
                ...prev[step],
                [fieldName]: jobDetails
            }
        }));
    }



    const setStepValidity = useCallback((step: MonthlyReportSteps | UnderwritingSteps, isValid: boolean) => {
        // console.log('changing step validity', step, "isValid", isValid)
        // formStepsValidityRef.current[step] = isValid;
        setFormStepsValidity({
            ...formStepsValidity,
            [step]: isValid
        })
    }, [formStepsValidity]);

    const formRefs = useRef({});

    const navigate = useNavigate();

    const handleAndSetFormStatuses = (formStatuses) => {
        const formStatusesObj = formStatuses.reduce((prev, curVal) => {
            prev[curVal.step] = curVal.status;
            return prev;
        }, {});
        setFormCompleteStatus(formStatusesObj);
    }

    const handleInitialValues = (formValues = {}) => {
        let initialValues = { ...formValues };
        setFormData({
            ...initialValues
        })
    }

    useEffect(() => {
        (async () => {
            try {
                await fetchFormData();
            } catch (error) {
                console.log('error', error);
            } finally {
                setLoading(false)
            }
        })();
    }, []);

    const changeUploadStatus = (keyName: string, uploadStatus: uploadStates) => {
        setFileUploadingStatus((prev) => ({
            ...prev,
            [keyName]: uploadStatus
        }));
        if (uploadStatus == uploadStates.uploading) {
            setUploadPendingItems((prevUploads) => [...prevUploads, { uniqueKey: keyName, status: 'uploading' }]);
        } else {
            // oldPendingItems.delete(keyName as any);
            setUploadPendingItems((prevUploads) => prevUploads.filter(upload => upload.uniqueKey !== keyName));
        }
        // setUploadPendingItems(Array.from(oldPendingItems))
    }

    const getUploadStatus = (keyName: string) => {
        // console.log('keyName', keyName, fileUploadingStatus)
        // console.log('fileUploadingStatus', fileUploadingStatus[keyName])
        return fileUploadingStatus[keyName] ?? null;
    }


    const fetchFormData = async () => {
        try {
            const response: any = await RestS.fetchMonthlyReportFormData();
            if (response?.success) {
                handleInitialValues(response?.formData?.data);
                const otherFormDetails = _.cloneDeep(response?.formData);
                delete otherFormDetails['data'];
                setExtraFormDetails({
                    ...otherFormDetails,
                    formType: LendingJobType.monthlyReport
                });
                handleAndSetFormStatuses(response?.formStatuses);
            }
        } catch (error) {
            console.log('error', error);
        }
    }


    useEffect(() => {
        (async () => {
            try {
                await fetchFormData();
            } catch (error) {
                console.log('error', error);
            } finally {
                setLoading(false)
            }
        })();
    }, []);


    const sendFileJobToApi = async (step: MonthlyReportSteps, fieldName: string, jobDetails: any) => {
        try {
            console.log('underwritingFileJobsRef.current', underwritingFileJobsRef.current)
            console.log('underwritingFileJobs', underwritingFileJobs)
            const latestJobDetails = underwritingFileJobsRef.current[step]?.[fieldName];
            console.log('latestJobDetails', latestJobDetails)
            const updatedJobDetails = {
                ...latestJobDetails,
                ...jobDetails,
                step: step,
                formFieldName: fieldName,
            }
            console.log('updatedJobDetails', updatedJobDetails)
            const data: any = await RestS.sendUnderwritingUploadJob({
                jobDetails: updatedJobDetails,
                formId: extraFormDetails?.id,
                formType: ApplicationContextType.monthlyReport
            });

            setUnderwritingFileJobs((prev) => ({
                ...prev,
                [step]: {
                    ...prev[step],
                    [fieldName]: {
                        ...prev[step][fieldName],
                        ...data
                    }
                }
            }))
            return data
        } catch (error) {
            console.log('error', error)
        }
    }

    const fetchStatementAnalysisJobs = async () => {
        try {
            const data: any = await RestS.fetchStatementAnalysisJob(ApplicationContextType.monthlyReport);
            console.log('statement analysis jobs', data);
            // if (data?.success) {
            if (data?.data?.length) {
                const allJobs = data.data;
                const allJobsObj = allJobs.reduce((prev, curVal) => {
                    if (!prev[curVal.taskId]) {
                        prev[curVal.taskId] = {
                            [curVal.jobType]: curVal
                        };
                    } else {
                        prev[curVal.taskId] = {
                            ...prev[curVal.taskId],
                            [curVal.jobType]: curVal
                        }
                    }
                    return prev;
                }, {});
                setStatementAnalysisJobs(allJobsObj);
            } else {
                setStatementAnalysisJobs({})
            }
        } catch (error) {
            console.log('error', error);
        }
    }

    const fetchRevenueClassificationJobs = async (fyYear?: string) => {
        try {
            if (fyYear) {
                const result = await RestS.fetchRevenueClassificationResult(fyYear) as any;
                console.log('revenue classification job for', fyYear, result);
                setRevenueClassificationJobs(prev => ({
                    ...prev,
                    [fyYear]: result?.data ?? null,
                }));
            } else {
                const fyYears = getLastThreeFinancialYears().map((fy) => fy.financialYear);
                const results = await Promise.all(
                    fyYears.map((fy) => RestS.fetchRevenueClassificationResult(fy) as Promise<any>)
                );
                const jobsByFyYear = fyYears.reduce((acc, fy, i) => {
                    acc[fy] = results[i]?.data ?? null;
                    return acc;
                }, {} as { [fyYear: string]: any });
                console.log('revenue classification jobs', jobsByFyYear);
                setRevenueClassificationJobs(jobsByFyYear);
            }
        } catch (error) {
            console.log('error fetching revenue classification jobs', error);
        }
    }

    const fetchUnderwritingFileJobs = async () => {
        try {
            const data: any = await RestS.fetchUploadedJobs(ApplicationContextType.monthlyReport, extraFormDetails?.monthYear);
            if (data?.data?.length) {
                const allJobs = data.data;
                const underwritingFileJobs = allJobs?.reduce((prev, curVal) => {
                    if (!prev[curVal.step]) {
                        prev[curVal.step] = {
                            [curVal.formFieldName]: curVal
                        }
                    } else {
                        prev[curVal.step] = {
                            ...prev[curVal.step],
                            [curVal.formFieldName]: curVal
                        }
                    }
                    return prev;
                }, {
                    [MonthlyReportSteps.connectAccounts]: {},
                    [MonthlyReportSteps.bankStatement]: {},
                    [MonthlyReportSteps.businessUpdates]: {},
                    [MonthlyReportSteps.salesMarketing]: {},
                    [MonthlyReportSteps.gst]: {},
                    [MonthlyReportSteps.recipients]: {},
                });
                console.log('underwritingFileJobs', underwritingFileJobs)
                setUnderwritingFileJobs(underwritingFileJobs);
            }
        } catch (error) {
            console.log('error', error)
        }
    }

    const checkAndSetJourneyStatus = async () => {
        const { coldState, underwritingFormData, allowGetStarted, userDetails } = await RestS.checkMonthlyReportJourney();
        if (coldState == UnderwritingColdState.accessDenied) {
            navigate('/404', {
                replace: true
            })
        } else if (coldState == UnderwritingColdState.completed) {
            navigate(monthlyReportRoutes.monthlyReportHome, {
                replace: true,
            })
        }
    }

    useEffect(() => {
        if (refreshStatementJobs) {
            console.log('refreshing statement jobs')
            fetchStatementAnalysisJobs();
            dispatch(setRefreshStatementJobs(false));
        }
    }, [refreshStatementJobs])

    useEffect(() => {
        (async () => {
            try {
                await Promise.all([
                    fetchUnderwritingFileJobs(),
                    fetchStatementAnalysisJobs(),
                    fetchGstAnalysisJobs(),
                    checkAndSetJourneyStatus(),
                    fetchGstAnalysisJobs(),
                    fetchRevenueClassificationJobs(),
                ])
            } catch (error) {
                console.log('error', error);
            } finally {
                setLoading(false)
            }
        })();
    }, []);


    const clearRevenueClassificationJob = (fyYear: string) => {
        setRevenueClassificationJobs(prev => ({
            ...prev,
            [fyYear]: {
                ...prev[fyYear],
                classificationErrorDetails: null,
            },
        }));
    };

    const clearAllRevenueClassificationData = (fyYear: string) => {
        setRevenueClassificationJobs(prev => {
            const next = { ...prev };
            delete next[fyYear];
            return next;
        });
    };

    useEffect(() => {
        if (refreshRevenueClassification) {
            fetchRevenueClassificationJobs(refreshRevenueClassification);
            dispatch(setRefreshRevenueClassification(null));
        }
    }, [refreshRevenueClassification])

    useEffect(() => {
        if (refreshFormData) {
            console.log('refreshing form data')
            fetchFormData().then(() => {
                fetchStatementAnalysisJobs();
            });
            dispatch(setRefreshFormData(false))
        }
    }, [refreshFormData])


    return (
        <MonthlyReportFormContext.Provider value={{ formData, formStepsValidity, setFormData, formRefs, setStepValidity, formCompleteStatus, handleAndSetFormStatuses, changeUploadStatus, uploadingPendingItems, getUploadStatus, underwritingFileJobs, handleUnderwritingFileJobs, sendFileJobToApi, statementAnalysisJobs, fetchUnderwritingFileJobs, fetchStatementAnalysisJobs, extraFormDetails, brandCategoriesObj, gstAnalysisJobs, setGstAnalysisJobs, fetchGstAnalysisJobs, revenueClassificationJobs, fetchRevenueClassificationJobs, clearRevenueClassificationJob, clearAllRevenueClassificationData }}>
            {loading ?
                <div className={styles.loadingContainer}>
                    <CircularLoader />
                </div>
                : children}
        </MonthlyReportFormContext.Provider>
    )
}

export default MonthlyReportFromProvider;